import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiShield } from 'react-icons/fi';
import { useToast } from '../contexts/ToastContext';
import apiClient, { endpoints } from '../api/client';

const RoleTemplateManager = ({ onRoleSelect, selectedRoleId }) => {
  const { showToast } = useToast();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    role_name: '',
    display_name: '',
    description: '',
    permissions: {
      users: { create: false, read: false, update: false, delete: false },
      certificates: { create: false, read: false, update: false, delete: false },
      tags: { create: false, read: false, update: false, delete: false },
      reminders: { create: false, read: false, update: false, delete: false },
      templates: { create: false, read: false, update: false, delete: false },
      reports: { create: false, read: false, update: false, delete: false },
      settings: { create: false, read: false, update: false, delete: false },
      audit_logs: { create: false, read: false, update: false, delete: false },
      sub_admins: { create: false, read: false, update: false, delete: false }
    }
  });

  const entities = [
    { key: 'users', label: 'Clients' },
    { key: 'certificates', label: 'Certificates' },
    { key: 'tags', label: 'Tags' },
    { key: 'reminders', label: 'Reminders' },
    { key: 'templates', label: 'Templates' },
    { key: 'reports', label: 'Reports' },
    { key: 'settings', label: 'Settings' },
    { key: 'audit_logs', label: 'Audit Logs' },
    { key: 'sub_admins', label: 'Sub Admins' }
  ];

  const permissions = ['create', 'read', 'update', 'delete'];

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(endpoints.subAdmin.roles);
      setRoles(response.data.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      showToast('Failed to load roles.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (entity, permission, value) => {
    console.log({
      ...formData.permissions,
      [entity]: {
        ...formData.permissions[entity],
        [permission]: value
      }
    });

    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [entity]: {
          ...prev.permissions[entity],
          [permission]: value
        }
      }
    }));
  };

  const startEdit = (role) => {
    console.log("role", role)
    setEditingRole(role);
    setFormData({
      role_name: role.role_name,
      display_name: role.display_name,
      description: role.description,
      permissions: role.permissions
    });
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingRole(null);
    setShowAddForm(false);
    setFormData({
      role_name: '',
      display_name: '',
      description: '',
      permissions: {
        users: { create: false, read: false, update: false, delete: false },
        certificates: { create: false, read: false, update: false, delete: false },
        tags: { create: false, read: false, update: false, delete: false },
        reminders: { create: false, read: false, update: false, delete: false },
        templates: { create: false, read: false, update: false, delete: false },
        reports: { create: false, read: false, update: false, delete: false },
        settings: { create: false, read: false, update: false, delete: false },
        audit_logs: { create: false, read: false, update: false, delete: false },
        sub_admins: { create: false, read: false, update: false, delete: false }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate role name uniqueness
    const existingRole = roles.find(role => 
      role.role_name.toLowerCase() === formData.role_name.toLowerCase() && 
      (!editingRole || role.id !== editingRole.id)
    );
    
    if (existingRole) {
      showToast('A role with this name already exists', 'error');
      return;
    }
    
    try {
      let response;
      if (editingRole) {
        response = await apiClient.put(endpoints.subAdmin.roleById(editingRole.id), { ...formData, role_name: formData.role_name.toLowerCase().replaceAll(" ", "_") });
        showToast('Role template updated successfully', 'success');
      } else {
        response = await apiClient.post(endpoints.subAdmin.roles, { ...formData, role_name: formData.role_name.toLowerCase().replaceAll(" ", "_") });
        showToast('Role template created successfully', 'success');
      }
      
      if (response.data.success) {
        fetchRoles();
        cancelEdit();
      }
    } catch (error) {
      console.error('Error saving role:', error);
      showToast(error.response?.data?.message || 'Failed to save role.', 'error');
    }
  };

  const handleDelete = async (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (role?.is_default) {
      showToast('Cannot delete default system roles', 'error');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await apiClient.delete(endpoints.subAdmin.roleById(roleId));
      if (response.data.success) {
        showToast('Role template deleted successfully', 'success');
        fetchRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      showToast(error.response?.data?.message || 'Failed to delete role.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center pt-5">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Role Assignments</h3>
          <p className="text-sm text-gray-600">Add and manage roles with predefined permissions</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
        >
          <FiPlus className="h-4 w-4 mr-2" />
          Add Role
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-gray-50 p-6 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-medium text-gray-900">
              {editingRole ? 'Edit Role' : 'Add New Role'}
            </h4>
            <button
              onClick={cancelEdit}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Role Name</label>
                <input
                  type="text"
                  required
                  value={formData.role_name}
                  onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                  disabled={editingRole?.is_default}
                  className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                  placeholder="e.g., manager"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Display Name</label>
                <input
                  type="text"
                  required
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                  placeholder="e.g., Manager"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                placeholder="Role description..."
              />
            </div>

            {/* Permissions Matrix */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Permissions Matrix</label>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entity
                      </th>
                      {permissions.map(permission => (
                        <th key={permission} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {permission}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {entities.map(entity => (
                      <tr key={entity.key}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {entity.label}
                        </td>
                        {permissions.map(permission => (
                          <td key={permission} className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={formData.permissions[entity.key]?.[permission] || false}
                              onChange={(e) => handlePermissionChange(entity.key, permission, e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                <FiSave className="h-4 w-4 mr-2" />
                Save Template
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Role Assignments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role) => (
          <div
            key={role.id}
            className={`bg-white border rounded-lg p-4 hover:shadow-md transition-all duration-200 cursor-pointer ${
              selectedRoleId === role.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            }`}
            onClick={() => onRoleSelect && onRoleSelect(role)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <FiShield className="h-5 w-5 text-blue-600 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{role.display_name}</h4>
                  <p className="text-xs text-gray-500">{role.role_name}</p>
                </div>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startEdit(role);
                  }}
                  className="text-gray-400 hover:text-blue-600 p-1"
                >
                  <FiEdit2 className="h-4 w-4" />
                </button>
                {!role.is_default && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(role.id);
                    }}
                    className="text-gray-400 hover:text-red-600 p-1"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mt-2">{role.description}</p>
            
            <div className="mt-3">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                role.is_default 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {role.is_default ? 'System' : 'Custom'}
              </span>
            </div>
            
            {/* Permission Summary */}
            <div className="mt-3 text-xs text-gray-500">
              {Object.entries(role.permissions || {}).filter(([_, perms]) => 
                Object.values(perms).some(p => p)
              ).length} modules configured
            </div>
          </div>
        ))}
      </div>

      {roles.length === 0 && (
        <div className="text-center py-12">
          <FiShield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Roles Configured</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating your first role.</p>
        </div>
      )}
    </div>
  );
};

export default RoleTemplateManager;